import React from "react";
import { THEMES } from "./registry";
import { isThemeId, type Theme, type ThemeId } from "./types";

/** Shared with the inline no-FOUC script in public/index.html. */
export const THEME_STORAGE_KEY = "ww-theme";
export const PULSE_STORAGE_KEY = "ww-theme-seen";

/** A switch in flight: which film is leaving, and which is arriving. */
export type ThemeTransitionState = { from: ThemeId; to: ThemeId };

/**
 * How long the wipe takes to cover the page, and to clear it again.
 *
 * The theme is committed at COVER_MS — the whole point of deferring it is that
 * the swap happens behind the cover, so you never see one film's backdrop
 * under another's typography. ThemeTransition animates to the same numbers;
 * they are exported so the two cannot drift apart.
 */
export const COVER_MS = 420;
export const UNCOVER_MS = 520;

type ThemeContextValue = {
  theme: Theme;
  themeId: ThemeId;
  setTheme: (id: ThemeId) => void;
  /** Non-null while a wipe is playing. */
  transition: ThemeTransitionState | null;
  /** True on a visitor's first ever load, until they interact with the switcher. */
  showPulse: boolean;
  dismissPulse: () => void;
};

const ThemeContext = React.createContext<ThemeContextValue | null>(null);

const readStoredTheme = (): ThemeId | null => {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isThemeId(stored) ? stored : null;
  } catch {
    // Private browsing or blocked storage — fall through to the default.
    return null;
  }
};

/** First impression for anyone arriving without a stored choice. */
const DEFAULT_THEME: ThemeId = "matrix";

/**
 * Resolves the active theme.
 *
 * The inline script in index.html has normally already resolved and stamped a
 * theme before React mounts, so this reads that decision back. The fallbacks
 * matter only when that script did not run.
 */
const resolveInitialTheme = (): ThemeId => {
  if (typeof document === "undefined") return DEFAULT_THEME;
  const stamped = document.documentElement.dataset.theme;
  if (isThemeId(stamped)) return stamped;
  return readStoredTheme() ?? DEFAULT_THEME;
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [themeId, setThemeId] = React.useState<ThemeId>(resolveInitialTheme);
  const [transition, setTransition] =
    React.useState<ThemeTransitionState | null>(null);
  const [showPulse, setShowPulse] = React.useState(false);

  /*
   * Switching is deferred rather than immediate.
   *
   * A crossfade could not hide the seam: theme tokens are global, so the
   * instant the root attribute flips, the outgoing backdrop is repainted in
   * the incoming film's palette while still on screen. Covering the page
   * first, swapping underneath, then clearing means neither film is ever seen
   * wearing the other's colours.
   */
  const setTheme = React.useCallback(
    (id: ThemeId) => {
      if (id === themeId || transition) return;

      const reduce =
        typeof window !== "undefined" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (reduce) {
        setThemeId(id);
        return;
      }

      setTransition({ from: themeId, to: id });
    },
    [themeId, transition]
  );

  React.useEffect(() => {
    if (!transition) return;

    // Commit behind the cover, then clear once the uncover has played out.
    const commit = window.setTimeout(() => setThemeId(transition.to), COVER_MS);
    const done = window.setTimeout(
      () => setTransition(null),
      COVER_MS + UNCOVER_MS
    );

    return () => {
      window.clearTimeout(commit);
      window.clearTimeout(done);
    };
  }, [transition]);

  // Keep <html data-theme> and storage in sync with state.
  React.useEffect(() => {
    document.documentElement.dataset.theme = themeId;
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, themeId);
    } catch {
      // Non-fatal: the theme still applies for this session.
    }
  }, [themeId]);

  // Pull in the active theme's display font on demand. Links are left in place
  // once added so switching back and forth does not refetch.
  React.useEffect(() => {
    const href = THEMES[themeId].fontHref;
    if (!href || document.querySelector(`link[href="${href}"]`)) return;

    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    document.head.appendChild(link);
  }, [themeId]);

  // The pulse is the only cue that four other themes exist, so it fires once
  // ever — then never again for that visitor.
  React.useEffect(() => {
    let seen = true;
    try {
      seen = window.localStorage.getItem(PULSE_STORAGE_KEY) === "1";
    } catch {
      // Cannot persist "seen", so skip the pulse rather than repeat it forever.
    }
    if (seen) return;

    setShowPulse(true);
    const timer = window.setTimeout(() => setShowPulse(false), 6000);
    return () => window.clearTimeout(timer);
  }, []);

  const dismissPulse = React.useCallback(() => {
    setShowPulse(false);
    try {
      window.localStorage.setItem(PULSE_STORAGE_KEY, "1");
    } catch {
      // Non-fatal.
    }
  }, []);

  const value = React.useMemo<ThemeContextValue>(
    () => ({
      theme: THEMES[themeId],
      themeId,
      setTheme,
      transition,
      showPulse,
      dismissPulse,
    }),
    [themeId, setTheme, transition, showPulse, dismissPulse]
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextValue => {
  const ctx = React.useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within a ThemeProvider");
  return ctx;
};
